import type { Step } from '@eip/engine';
import type { DocEntry, Settings } from '../state/store';
import type { FilmInfo, FilmsExportResult } from '../worker/protocol';
import { buildReport, reportJson } from './report';
import { reportHtml } from './report-html';
import { createZip } from './zip';

export interface FilmsDeps {
  exportFilms(id: string, generation: number, onFilm: (f: FilmInfo) => void): Promise<FilmsExportResult>;
  /** Called as each film lands, with the number written so far and the total. */
  onProgress?(done: number, total: number): void;
  now?: Date;
}

/** Lower-cased, every run of non-alphanumerics collapsed to one `-`, trimmed.
 *  Film and archive names are typed by hand at the press and land on
 *  case-insensitive volumes, so they carry nothing but `[a-z0-9-]`. */
export function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** `skull-job_02_dark-blue.png` — job, press order, plate. A job with no name
 *  (or one that slugs away entirely) becomes `job`. */
export function filmFileName(job: string, index: number, name: string): string {
  return `${slug(job) || 'job'}_${String(index).padStart(2, '0')}_${slug(name)}.png`;
}

/** Why a document has no films to export: nothing built at all, or plates built
 *  from an older DTF run than the one the doc is showing now. */
export type SepReadiness = 'ok' | 'none' | 'stale';

/**
 * Whether the worker still holds plates this document can be filmed from.
 *
 * `stale` is the debounce window: a DTF run landing schedules a separation
 * rebuild, and until that rebuild lands the plates (and the worker's cached
 * films) describe the PREVIOUS output while the report's `output` section
 * describes the new one. Filming that would hand the press films of artwork
 * nobody is looking at any more.
 */
export function separationReadiness(doc: DocEntry): SepReadiness {
  const sep = doc.sep;
  if (!sep || sep.plates.length === 0 || sep.building || sep.error || sep.builtGeneration === undefined) return 'none';
  if (!doc.run || sep.runGeneration !== doc.run.generation) return 'stale';
  return 'ok';
}

/** Only a doc whose separation has been BUILT from the CURRENT run has films to
 *  export. `generation` is the build the worker still holds the plates for. */
function builtSep(doc: DocEntry): { sep: NonNullable<DocEntry['sep']>; generation: number } | null {
  const sep = doc.sep;
  if (separationReadiness(doc) !== 'ok' || !sep || sep.builtGeneration === undefined) return null;
  return { sep, generation: sep.builtGeneration };
}

/** True when `doc` is ready to hand to `addFilmsToZip`; the batch uses it to skip the rest. */
export function hasBuiltSeparation(doc: DocEntry): boolean { return builtSep(doc) !== null; }

const at = (prefix: string, name: string) => (prefix ? `${prefix}/${name}` : name);

/**
 * Writes one document's whole film package into an open ZIP: a 1-bit PNG per
 * plate, the imposed `films.pdf`, a `proof.png` and the job ticket (HTML+JSON),
 * all under `prefix/` (or at the root when `prefix` is empty).
 *
 * Each film is added from inside `onFilm` as the worker streams it, so only the
 * one film the worker just handed over is alive as a standalone buffer — a
 * 7-plate job at 720 dpi would otherwise hold every film at once on top of the
 * copy the ZIP writer keeps.
 *
 * Throws when the doc has no built separation (or a stale one), when the worker
 * reports the export as superseded (`cancelled`), when fewer films arrived than
 * the worker sent, or on any worker failure; the batch catches that and records
 * the doc as skipped.
 */
export async function addFilmsToZip(
  zip: ReturnType<typeof createZip>, doc: DocEntry, steps: Step[], settings: Settings, deps: FilmsDeps, prefix: string,
): Promise<{ count: number; job: string }> {
  const built = builtSep(doc);
  if (!built) {
    throw new Error(separationReadiness(doc) === 'stale'
      ? `${doc.name}: the separation is stale — it was built from an earlier version of this artwork; rebuild it before exporting films`
      : `${doc.name}: no separation built — build the separation before exporting films`);
  }
  const { sep, generation } = built;
  const now = deps.now ?? new Date();
  // The one place the job-name fallback lives: film entries and the archive name
  // both use this slug.
  const job = slug(sep.separation.film.jobName) || 'job';
  let count = 0;
  const result = await deps.exportFilms(doc.id, generation, (film) => {
    zip.add(at(prefix, filmFileName(job, film.index, film.name)), film.bytes, { mtime: now });
    count++;
    deps.onProgress?.(count, film.total);
  });
  // A newer build superseded this export while it ran: its films describe a
  // separation nobody is looking at any more, so the package is thrown away
  // rather than handed to the press.
  if (result.cancelled) throw new Error(`${doc.name}: films export cancelled — the separation changed while it was exporting`);
  // `onFilm` is fire-and-forget across the worker boundary: the worker keeps
  // counting whether or not this side accepted a film, so a callback that threw
  // (or a film lost in transit) would otherwise ship a package quietly missing
  // a plate. The worker's own count is the only witness to that.
  if (count !== result.count) throw new Error(`${doc.name}: films export incomplete: ${count} of ${result.count} films received`);
  zip.add(at(prefix, 'films.pdf'), result.pdf, { mtime: now });
  zip.add(at(prefix, 'proof.png'), result.proof, { mtime: now });
  // Every folder is a self-contained job package: the ticket that describes
  // these films travels with them, whatever else the archive holds.
  const report = buildReport([doc], steps, settings, now);
  const enc = new TextEncoder();
  zip.add(at(prefix, 'report.html'), enc.encode(reportHtml(report)), { compress: true, mtime: now });
  zip.add(at(prefix, 'report.json'), enc.encode(reportJson(report)), { compress: true, mtime: now });
  return { count, job };
}

/** One document's films as a downloadable ZIP, named after the job. */
export async function exportFilmsZip(doc: DocEntry, steps: Step[], settings: Settings, deps: FilmsDeps): Promise<{ blob: Blob; filename: string; count: number }> {
  const zip = createZip();
  const { count, job } = await addFilmsToZip(zip, doc, steps, settings, deps, '');
  return { blob: await zip.finish(), filename: `${job}_films.zip`, count };
}
